import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import AutocompleteTextField from './AutocompleteTextField';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { isSuperAdminEmail } from '../config/adminEmails';

interface RankedMatch {
  category: string;
  player1: string;
  player2: string;
  player3: string;
  player4: string;
  winner: string;
}

const UpdateRankedMatches: React.FC = () => {
  const navigate = useNavigate();

  const [rankedMatches, setRankedMatches] = useState<RankedMatch[]>([
    {
      category: 'ganda_solo',
      player1: '',
      player2: '',
      player3: '',
      player4: '',
      winner: 'player1'
    }
  ]);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTitle, setDialogTitle] = useState('');
  const [dialogMessage, setDialogMessage] = useState('');
  const [dialogLoading, setDialogLoading] = useState(false);

  // Autocomplete state
  const [playerNames, setPlayerNames] = useState({
    ranked_single: [],
    ranked_double_solo: [],
    ranked_double_party: []
  });
  const [playerNamesLoading, setPlayerNamesLoading] = useState(false);

  // Check admin status
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const superAdminStatus = await isSuperAdminEmail(user.email || '');
        setIsSuperAdmin(superAdminStatus);
        setAdminEmail(user.email || '');
      } else {
        setIsSuperAdmin(false);
        setAdminEmail('');
      }
    });

    return () => unsubscribe();
  }, []);

  // Fetch player names for autocomplete
  useEffect(() => {
    const fetchPlayerNames = async () => {
      if (!isSuperAdmin) return;

      setPlayerNamesLoading(true);
      try {
        const response = await axios.get('/api/bookings?action=player-names');
        if (response.data) {
          setPlayerNames(response.data);
        }
      } catch (error) {
        console.error('Error fetching player names:', error);
      } finally {
        setPlayerNamesLoading(false);
      }
    };

    fetchPlayerNames();
  }, [isSuperAdmin]);

  const getAutocompleteOptions = (category: string) => {
    switch (category) {
      case 'tunggal':
        return playerNames.ranked_single;
      case 'ganda_solo':
        return playerNames.ranked_double_solo;
      case 'ganda_party':
        return playerNames.ranked_double_party;
      default:
        return [];
    }
  };

  const handleRankedFormChange = (matchIndex: number, field: string, value: string) => {
    setRankedMatches(prev => {
      const updated = [...prev];
      const match = updated[matchIndex];

      if (field === 'category') {
        // Clear player names when category changes
        match.category = value;
        match.player1 = '';
        match.player2 = '';
        match.player3 = '';
        match.player4 = '';
      } else {
        (match as any)[field] = value;
      }

      return updated;
    });
  };

  const addRankedMatch = () => {
    setRankedMatches(prev => [...prev, {
      category: 'ganda_solo',
      player1: '',
      player2: '',
      player3: '',
      player4: '',
      winner: 'player1'
    }]);
  };

  const removeRankedMatch = (matchIndex: number) => {
    if (rankedMatches.length > 1) {
      setRankedMatches(prev => prev.filter((_, idx) => idx !== matchIndex));
    }
  };

  const handleRankedSubmit = async () => {
    // Validate all matches first
    for (let i = 0; i < rankedMatches.length; i++) {
      const match = rankedMatches[i];

      if (!match.category) {
        setSubmitError(`Match ${i + 1}: Pilih kategori terlebih dahulu`);
        return;
      }

      if (!match.player1) {
        setSubmitError(`Match ${i + 1}: Mohon isi nama pemain 1`);
        return;
      }

      if (match.category === 'tunggal' && !match.player2) {
        setSubmitError(`Match ${i + 1}: Mohon isi nama pemain 2 (Tim B) untuk kategori tunggal`);
        return;
      }

      if (match.category === 'ganda_solo' && (!match.player2 || !match.player3 || !match.player4)) {
        setSubmitError(`Match ${i + 1}: Mohon isi semua nama pemain untuk kategori ganda solo (Tim A: 2 orang, Tim B: 2 orang)`);
        return;
      }

      if (match.category === 'ganda_party' && (!match.player2 || !match.player3 || !match.player4)) {
        setSubmitError(`Match ${i + 1}: Mohon isi semua nama pemain untuk kategori ganda party (Tim A: 2 orang, Tim B: 2 orang)`);
        return;
      }
    }

    setSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    setDialogOpen(true);
    setDialogTitle('Sedang memproses ranked match');
    setDialogMessage('Silakan tunggu, data sedang dikirim ke server...');
    setDialogLoading(true);

    try {
      // Process all matches sequentially
      for (let i = 0; i < rankedMatches.length; i++) {
        const rankedForm = rankedMatches[i];

        let sheetName = '';
        switch (rankedForm.category) {
          case 'tunggal':
            sheetName = 'ranked_single';
            break;
          case 'ganda_solo':
            sheetName = 'ranked_double_solo';
            break;
          case 'ganda_party':
            sheetName = 'ranked_double_party';
            break;
          default:
            throw new Error('Kategori tidak valid');
        }

        const requestData = {
          sheetName,
          data: {
            player1: rankedForm.player1,
            player2: rankedForm.player2 || '',
            player3: rankedForm.player3 || '',
            player4: rankedForm.player4 || '',
            winner: rankedForm.winner,
            category: rankedForm.category,
            adminEmail: adminEmail,
            timestamp: new Date().toISOString()
          }
        };

        console.log(`=== RANKED MATCH REQUEST (${i + 1}/${rankedMatches.length}) ===`);
        console.log('Data yang dikirim ke API:', requestData);
        console.log('============================');

        const response = await axios.post('/api/bookings?action=ranked-score', requestData);

        if (response.data && response.data.success !== false) {
          if (response.data.eloUpdate) {
            console.log(`Elo Rating Update (Match ${i + 1}):`, response.data.eloUpdate);
          }
        } else {
          const detail = response.data?.message || response.data?.error || 'Gagal mengirim data';
          throw new Error(`Match ${i + 1}: ${detail}`);
        }
      }

      // All matches submitted successfully
      setSubmitSuccess(true);
        setDialogTitle('Berhasil');
        setDialogMessage('Semua pertandingan berhasil disubmit. Rating Elo telah terupdate.');
        setDialogLoading(false);

        setRankedMatches([{
          category: 'ganda_solo',
        player1: '',
        player2: '',
        player3: '',
        player4: '',
        winner: 'player1'
      }]);

    } catch (err: any) {
      console.error('Error submitting ranked matches:', err);
      const detail = err?.response?.data?.message || err?.response?.data?.error || err.message || 'Terjadi kesalahan saat mengirim data';
      setSubmitError(detail);
      setDialogTitle('Gagal');
      setDialogMessage(detail);
      setDialogLoading(false);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isSuperAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            Update Win-Loss Ranked Match
          </Typography>
          <Alert severity="info">
            Hanya superadmin yang dapat mengakses halaman ini. Silakan login dengan akun superadmin.
          </Alert>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Paper sx={{ p: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: 'primary.main', textAlign: 'center' }}>
          🏆 Update Win-Loss Ranked Match
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
          Admin: {adminEmail} | Data akan disimpan ke spreadsheet "update-score" dengan sistem Elo rating
        </Typography>

        {playerNamesLoading && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <CircularProgress size={16} sx={{ mr: 1 }} />
            Memuat daftar nama pemain untuk autocomplete...
          </Alert>
        )}

        {submitSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Hasil pertandingan berhasil diupdate dengan sistem Elo rating!
          </Alert>
        )}

        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {submitError}
          </Alert>
        )}

        <Dialog open={dialogOpen} onClose={() => { if (!dialogLoading) setDialogOpen(false); }}>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogContent>
            <DialogContentText>{dialogMessage}</DialogContentText>
            {dialogLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <CircularProgress />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)} disabled={dialogLoading}>Tutup</Button>
          </DialogActions>
        </Dialog>

        {/* Daftar Match */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            💡 Anda dapat input lebih dari 1 match sekaligus. Gunakan tombol <strong>+ Tambah Match</strong> untuk menambah, dan klik tombol <strong>×</strong> pada setiap match untuk menghapus (jika lebih dari 1). Match akan diproses berurutan dari atas ke bawah.
          </Typography>
        </Box>

        {/* Loop through all matches */}
        {rankedMatches.map((rankedForm, matchIndex) => (
          <Box key={matchIndex} sx={{ mb: 4, pb: 3, border: '2px dashed rgba(99, 170, 255, 0.3)', borderRadius: 2, p: 2, position: 'relative' }}>
            {/* Match Header dan Delete Button */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                Match {matchIndex + 1}
              </Typography>
              {rankedMatches.length > 1 && (
                <IconButton
                  onClick={() => removeRankedMatch(matchIndex)}
                  color="error"
                  size="small"
                  title="Hapus match ini"
                  sx={{
                    bgcolor: 'error.light',
                    '&:hover': { bgcolor: 'error.main', color: 'white' }
                  }}
                >
                  <CloseIcon />
                </IconButton>
              )}
            </Box>

            {/* Kategori Pertandingan */}
            <Grid item xs={12} sx={{ mb: 2 }}>
              {matchIndex === 0 && (
                <>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: 'text.primary' }}>
                    📋 Kategori Pertandingan
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    💡 <strong>Fitur Autocomplete:</strong> Ketik nama pemain untuk mendapatkan saran dari daftar pemain yang sudah terdaftar.
                    Anda tetap bisa mengetik nama pemain baru yang belum terdaftar di spreadsheet.
                  </Typography>
                </>
              )}
              <FormControl fullWidth>
                <InputLabel>Pilih Kategori</InputLabel>
                <Select
                  value={rankedForm.category}
                  label="Pilih Kategori"
                  onChange={(e) => handleRankedFormChange(matchIndex, 'category', e.target.value)}
                >
                  <MenuItem value="tunggal">🏸 Tunggal (1 vs 1)</MenuItem>
                  <MenuItem value="ganda_solo">👥 Ganda (Solo) (2 vs 2)</MenuItem>
                  <MenuItem value="ganda_party">🎉 Ganda (Party) (2 vs 2)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Tim A */}
            <Grid item xs={12}>
              <Box sx={{
                p: 2,
                border: '3px solid',
                borderColor: '#1976d2',
                borderRadius: 2,
                backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#121212' : '#ffffff',
                mb: 2,
                boxShadow: '0 2px 8px rgba(25, 118, 210, 0.2)'
              }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: '#1976d2' }}>
                  🔵 Tim A
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <AutocompleteTextField
                      label="Nama Pemain 1"
                      value={rankedForm.player1}
                      onChange={(value) => handleRankedFormChange(matchIndex, 'player1', value)}
                      options={getAutocompleteOptions(rankedForm.category)}
                      loading={playerNamesLoading}
                      required
                      placeholder="Masukkan nama pemain 1"
                    />
                  </Grid>

                  {/* Untuk Ganda: Pemain 2 Tim A */}
                  {(rankedForm.category === 'ganda_solo' || rankedForm.category === 'ganda_party') && (
                    <Grid item xs={12} md={6}>
                      <AutocompleteTextField
                        label="Nama Pemain 2"
                        value={rankedForm.player2}
                        onChange={(value) => handleRankedFormChange(matchIndex, 'player2', value)}
                        options={getAutocompleteOptions(rankedForm.category)}
                        loading={playerNamesLoading}
                        required
                        placeholder="Masukkan nama pemain 2"
                      />
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Grid>

            {/* Tim B */}
            <Grid item xs={12}>
              <Box sx={{
                p: 2,
                border: '3px solid',
                borderColor: '#dc004e',
                borderRadius: 2,
                backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#121212' : '#ffffff',
                mb: 2,
                boxShadow: '0 2px 8px rgba(220, 0, 78, 0.2)'
              }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: '#dc004e' }}>
                  🔴 Tim B
                </Typography>
                <Grid container spacing={2}>
                  {/* Untuk Tunggal: Pemain 2 Tim B */}
                  {rankedForm.category === 'tunggal' && (
                    <Grid item xs={12} md={6}>
                      <AutocompleteTextField
                        label="Nama Pemain 2"
                        value={rankedForm.player2}
                        onChange={(value) => handleRankedFormChange(matchIndex, 'player2', value)}
                        options={getAutocompleteOptions(rankedForm.category)}
                        loading={playerNamesLoading}
                        required
                        placeholder="Masukkan nama pemain 2"
                      />
                    </Grid>
                  )}

                  {/* Untuk Ganda: Pemain 3 & 4 Tim B */}
                  {(rankedForm.category === 'ganda_solo' || rankedForm.category === 'ganda_party') && (
                    <>
                      <Grid item xs={12} md={6}>
                        <AutocompleteTextField
                          label="Nama Pemain 3"
                          value={rankedForm.player3}
                          onChange={(value) => handleRankedFormChange(matchIndex, 'player3', value)}
                          options={getAutocompleteOptions(rankedForm.category)}
                          loading={playerNamesLoading}
                          required
                          placeholder="Masukkan nama pemain 3"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <AutocompleteTextField
                          label="Nama Pemain 4"
                          value={rankedForm.player4}
                          onChange={(value) => handleRankedFormChange(matchIndex, 'player4', value)}
                          options={getAutocompleteOptions(rankedForm.category)}
                          loading={playerNamesLoading}
                          required
                          placeholder="Masukkan nama pemain 4"
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              </Box>
            </Grid>

            {/* Hasil Pertandingan */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: 'success.main' }}>
                🏆 Hasil Pertandingan
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Pemenang</InputLabel>
                <Select
                  value={rankedForm.winner}
                  label="Pemenang"
                  onChange={(e) => handleRankedFormChange(matchIndex, 'winner', e.target.value)}
                >
                  <MenuItem value="player1">🔵 Tim A Menang</MenuItem>
                  <MenuItem value="player2">🔴 Tim B Menang</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Box>
        ))}

        {/* Tombol Tambah Match */}
        <Grid item xs={12}>
          <Button
            variant="outlined"
            color="primary"
            onClick={addRankedMatch}
            startIcon={<AddIcon />}
            fullWidth
            sx={{
              mb: 2,
              py: 1.2,
              fontSize: '1rem',
              fontWeight: 600,
              borderRadius: 2
            }}
          >
            + Tambah Match
          </Button>
        </Grid>

        {/* Tombol Submit */}
        <Grid item xs={12}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleRankedSubmit}
            disabled={submitting}
            fullWidth
            size="large"
            sx={{
              mt: 3,
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600,
              borderRadius: 2
            }}
          >
            {submitting ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Memproses...
              </>
            ) : (
              '🚀 Submit Hasil Pertandingan'
            )}
          </Button>
        </Grid>

        {/* Tombol Kembali */}
        <Grid item xs={12}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => navigate('/ranked-match')}
            fullWidth
            sx={{
              mt: 2,
              py: 1.2,
              fontSize: '1rem',
              fontWeight: 600,
              borderRadius: 2
            }}
          >
            ← Kembali ke Leaderboard
          </Button>
        </Grid>
      </Paper>
    </Box>
  );
};

export default UpdateRankedMatches;